// The jobs beneath a retainer: cost per job, and nothing else.
//
// Σ JOB COST HERE IS NOT THE RETAINER'S EXPENSE FIGURE ABOVE, and the two differ in one
// direction only. A job that bills its own income keeps the costs the CLIENT bears — they
// net against its own revenue — while the costs we ABSORB on it belong to the retainer
// (see queries/expenses.js). So a brokerage job's row shows its cost as those TWO figures,
// never one merged total: the part its charge is measured against, and the absorbed part
// that is inside the retainer's figure. A topic's whole cost is the retainer's, as it
// always was, and shows as one number.
//
// A job is added from HERE, not from the one-off projects screen: both the client and the
// retainer are already known on this page, so the form opens seeded with them and with
// income_source already 'retainer'. `JobModal` treats a job prop with no id as a new job
// (isNew), so the seed is a prefill and the save still goes through create.
//
// THE DEFAULT VIEW IS THE LIVE WORK, matching the one-off projects screen and the
// retainers list. An engagement that has run for two years has a long tail of finished
// topics, and a section that lists all of them buries the two or three actually being
// worked on. The toggle keeps them one click away.
//
// THE FILTER IS THE JOB'S OWN STATUS, never the retainer's. A finished job under a live
// retainer is ordinary — one topic ends, the engagement runs on — and a live job under a
// closed retainer is precisely the loose end worth seeing, which is why this screen shows
// the same two tabs whichever state the retainer above is in.
//
// The costs on BOTH tabs are already in the retainer's expense and profit tiles above.
// Nothing here is a filter over money; it is a filter over which rows are on screen.
// Split out of RetainerDetail.js when the toggle landed — the page was near the 300-line
// ceiling and this section is a self-contained piece of it.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import he from '../../i18n/he';
import DataTable from '../../components/DataTable';
import StatusBadge, { isJobDone } from '../../components/StatusBadge';
import { Cost } from '../../components/Money';
import Button from '../../components/Button';
import ViewToggle from '../../components/ViewToggle';
import { UnpaidBadge, ToRecoverBadge, OnRetainerBadge } from '../../components/ExpenseSplit';
import { JobTermCell } from '../../components/JobTerm';
import JobModal from '../../components/JobModal';
import JobStatusModal from '../../components/JobStatusModal';
import { engagementStrings } from '../../components/engagement';

export default function TopicsPanel({ retainer, topics, onChange }) {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [closing, setClosing] = useState(null);
  const [showDone, setShowDone] = useState(false);

  const rows = topics.filter((t) => isJobDone(t.status) === showDone);
  // The engagement's words: under a one-off project the badge names the project.
  const words = engagementStrings(retainer);

  const columns = [
    { key: 'title', label: he.jobs.jobTitle, primary: true, render: (r) => (
      <>
        {r.title}
        {r.billsOwnIncome && <span className="badge blue" style={{ marginInlineStart: 6 }}>{words.ownIncome}</span>}
      </>
    ) },
    { key: 'status', label: he.jobs.status, render: (r) => <StatusBadge kind="job" value={r.status} /> },
    // When each topic runs. This is the list the dates earn their place on: the topics
    // under one engagement, read side by side, where "which of these is running now"
    // and "which ran in March" are the questions being asked. The marker beside a range
    // means the topic falls outside the retainer's own term — worth seeing here, above
    // all, and still only a marker.
    { key: 'term', label: he.jobs.workTerm, render: (r) => <JobTermCell term={r.term} /> },
    // The job's whole cost, with the outstanding part flagged beside it only when there
    // is one — a column of zeros would be noise on every fully settled job. Unchanged on
    // the finished tab: a finished job's costs are still costs, and an unpaid one is
    // still owed to whoever fronted it.
    { key: 'cost', label: he.retainers.topicCost, className: 'num', render: (r) => (
      <>
        {/* A brokerage job's cost, split: what its charge is measured against (the job
            unit's own cost, the figure its margin is computed from), and — in the badge
            below — what we absorb, which nets against this retainer. Summing them gives
            the job's raw total; showing the sum would merge two different answers. */}
        {r.billsOwnIncome ? (
          <><span className="muted">{he.retainers.costAgainstCharge}</span> <Cost value={r.costOnJob} /></>
        ) : <Cost value={r.cost} />}
        <UnpaidBadge unpaid={r.costUnpaid} />
        {/* The part of this topic's cost that is coming back. A topic whose costs are all
            charged on cost the retainer nothing, and the bare total cannot say so. */}
        <ToRecoverBadge toRecover={r.costToRecover} />
        {/* On a job that bills its own margin, the costs we absorb net against THIS
            retainer, not against the job — the second half of the split above. Zero on
            a topic, whose whole cost is the retainer's anyway, and where the badge would
            say nothing. */}
        <OnRetainerBadge onRetainer={r.costOnRetainer} />
      </>
    ) },
    {
      key: 'close',
      label: '',
      action: true,
      render: (r) => (
        <Button small variant="subtle" onClick={(e) => { e.stopPropagation(); setClosing(r); }}>
          {isJobDone(r.status) ? he.jobs.reopenJob : he.jobs.closeJobAction}
        </Button>
      ),
    },
  ];

  return (
    <div className="card w-section">
      <div className="w-head">
        <h2>{he.retainers.topics}</h2>
        <div className="row">
          <ViewToggle
            value={showDone}
            onChange={setShowDone}
            offLabel={he.jobs.showOpenJobs}
            onLabel={he.jobs.showDoneJobs}
          />
          <Button small variant="ghost" onClick={() => setAdding(true)}>{he.retainers.addTopic}</Button>
        </div>
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        onRowClick={(r) => navigate(`/jobs/${r.id}`)}
        empty={{ text: showDone ? he.retainers.emptyDoneTopics : he.retainers.emptyTopics }}
      />
      {adding && (
        <JobModal
          job={{
            client_id: retainer.client_id,
            retainer_id: retainer.id,
            income_source: 'retainer',
          }}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); onChange(); }}
        />
      )}
      {/* The retainer reloads after a job's status changes so the section it lives in
          re-splits. No figure on the page moves — the tiles above are built from
          invoices and expenses, which this cannot touch. */}
      {closing && (
        <JobStatusModal
          job={{ ...closing, retainer_id: retainer.id }}
          onClose={() => setClosing(null)}
          onSaved={() => { setClosing(null); onChange(); }}
        />
      )}
    </div>
  );
}
